import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAdminAuditEvents } from 'hooks/useAdminAuditEvents';
import ChildRowsStatus from './ChildRowsStatus';
import EventRow from './EventRow';
import { CHILDREN_PAGE, type OpenEvent } from './tableColumns';

interface ImportChildRowsProps {
  importId: string;
  onOpen: OpenEvent;
}

/** Child rows of one `technician_import` parent, fetched only once its row is expanded (CA19).
 * Mounting the component IS the "on demand": no fetch happens before this renders it. */
export const ImportChildRows = ({ importId, onOpen }: ImportChildRowsProps) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { events, isLoading, isError, refetch, hasNextPage } = useAdminAuditEvents({ importId });

  if (isLoading || isError) return <ChildRowsStatus isError={!isLoading} onRetry={() => refetch()} />;

  const visible = showAll ? events : events.slice(0, CHILDREN_PAGE);
  const remaining = events.length - visible.length;

  return (
    <>
      {visible.map((child) => (
        <EventRow key={child.id} event={child} onOpen={onOpen} dense />
      ))}
      {(remaining > 0 || hasNextPage) && (
        <div role="row" className="border-b py-2 pl-8">
          <div role="cell" className="flex items-center gap-4 text-[12.5px] font-medium text-muted-foreground">
            {remaining > 0 && (
              <button type="button" onClick={() => setShowAll(true)} className="underline hover:text-foreground hover:no-underline">
                {t('Show the other row', { count: remaining })}
              </button>
            )}
            {/* Past one page (200) the expansion stops: the filtered view pages the rest (T9). */}
            {hasNextPage && (
              <Link to={`/admin/audit?importId=${encodeURIComponent(importId)}`} className="underline hover:text-foreground hover:no-underline">
                {t('View the whole import')}
              </Link>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImportChildRows;
